import React, { useState } from 'react';
import { MapPin, Mail, Phone, Clock, Send } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      // Send to backend API
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit contact form');
      }

      const result = await response.json();
      console.log('Contact form submitted:', result);

      toast.success('Message sent successfully! We\'ll get back to you soon.');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to send message. Please try again or email us directly at info@thevisaspot.com');
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="bg-gradient-to-r from-[#8BC34A] to-[#7CB342] text-white px-4 py-2 rounded-full text-sm font-semibold">
              Get In Touch
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Contact Us
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {/* Contact Information */}
          <div className="lg:col-span-1">
            <div className="space-y-6">
              {/* Address */}
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-white">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#1565C0] to-[#0D47A1] flex items-center justify-center mb-4">
                    <MapPin size={24} className="text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Visit Us</h3>
                  <p className="text-gray-600">
                    F.No-109, C B Eastern Homes,<br />
                    Above Bank of Maharashtra,<br />
                    Kamala Nagar, Dilsukhnagar,<br />
                    Hyderabad, Telangana-500068
                  </p>
                </CardContent>
              </Card>

              {/* Email */}
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-white">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#FF7043] to-[#F4511E] flex items-center justify-center mb-4">
                    <Mail size={24} className="text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Email Us</h3>
                  <a href="mailto:info@thevisaspot.com" className="text-[#1565C0] hover:text-[#FF7043] transition-colors">
                    info@thevisaspot.com
                  </a>
                  <br />
                  <a href="https://www.thevisaspot.com" target="_blank" rel="noopener noreferrer" className="text-[#1565C0] hover:text-[#FF7043] transition-colors">
                    www.thevisaspot.com
                  </a>
                </CardContent>
              </Card>

              {/* Phone */}
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-white">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#8BC34A] to-[#7CB342] flex items-center justify-center mb-4">
                    <Phone size={24} className="text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Call Us</h3>
                  <a href="tel:9052808055" className="text-[#1565C0] hover:text-[#FF7043] transition-colors text-lg font-semibold">
                    +91 905-280-8055
                  </a>
                </CardContent>
              </Card>

              {/* Office Hours */}
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-white">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#1565C0] to-[#0D47A1] flex items-center justify-center mb-4">
                    <Clock size={24} className="text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Office Hours</h3>
                  <p className="text-gray-600">
                    Monday - Friday: 9:00 AM - 6:00 PM<br />
                    Saturday: 10:00 AM - 4:00 PM<br />
                    Sunday: Closed
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="border-0 shadow-2xl bg-white">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    {/* Name */}
                    <div>
                      <Label htmlFor="contact-name" className="text-gray-700 font-semibold mb-2">
                        Full Name *
                      </Label>
                      <Input
                        id="contact-name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter your full name"
                        className="mt-1 border-gray-300 focus:border-[#1565C0] focus:ring-[#1565C0]"
                        required
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <Label htmlFor="contact-email" className="text-gray-700 font-semibold mb-2">
                        Email Address *
                      </Label>
                      <Input
                        id="contact-email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your.email@example.com"
                        className="mt-1 border-gray-300 focus:border-[#1565C0] focus:ring-[#1565C0]"
                        required
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <Label htmlFor="contact-phone" className="text-gray-700 font-semibold mb-2">
                        Phone Number
                      </Label>
                      <Input
                        id="contact-phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+1 (234) 567-890"
                        className="mt-1 border-gray-300 focus:border-[#1565C0] focus:ring-[#1565C0]"
                      />
                    </div>

                    {/* Subject */}
                    <div>
                      <Label htmlFor="contact-subject" className="text-gray-700 font-semibold mb-2">
                        Subject
                      </Label>
                      <Input
                        id="contact-subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="How can we help?"
                        className="mt-1 border-gray-300 focus:border-[#1565C0] focus:ring-[#1565C0]"
                      />
                    </div>

                    {/* Message */}
                    <div className="md:col-span-2">
                      <Label htmlFor="contact-message" className="text-gray-700 font-semibold mb-2">
                        Message *
                      </Label>
                      <Textarea
                        id="contact-message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us more about your inquiry..."
                        rows={6}
                        className="mt-1 border-gray-300 focus:border-[#1565C0] focus:ring-[#1565C0]"
                        required
                      />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <Button 
                    type="submit"
                    size="lg"
                    className="w-full bg-gradient-to-r from-[#1565C0] to-[#0D47A1] hover:from-[#0D47A1] hover:to-[#1565C0] text-white py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    Send Message
                    <Send className="ml-2" size={20} />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
